/**
 * Per-module string tables — port of `L10nTable` in `resto_app/flutter_app/lib/app/preferences.dart`.
 *
 *   const ownerStrings = l10nTable({ ...en }, { ...te });
 *   const tr = useTr();
 *   tr(ownerStrings, 'filtersN', { n: 3 });
 *
 * Placeholders use `{name}` and are filled from `args`. A missing Telugu key falls back to English.
 */

/** UI languages: English (default) and Telugu. */
export type Lang = 'en' | 'te';

export type TrArgs = Record<string, unknown>;

export interface L10nTable<K extends string = string> {
    en: Record<K, string>;
    te: Partial<Record<K, string>>;
}

export const l10nTable = <K extends string>(en: Record<K, string>, te: Partial<Record<K, string>> = {}): L10nTable<K> => ({
    en,
    te,
});

export function translate<K extends string>(lang: Lang, table: L10nTable<K>, key: K, args: TrArgs = {}): string {
    let text: string = (lang === 'te' ? table.te[key] : undefined) ?? table.en[key] ?? key;
    for (const [name, value] of Object.entries(args)) text = text.split(`{${name}}`).join(String(value ?? ''));
    return text;
}

// The language outside React: `LangProvider` keeps it current, so code that runs from an event
// handler rather than a render (`errorText`, file readers) can still answer in the right language.
let activeLang: Lang = 'en';

export const setActiveLang = (lang: Lang): void => {
    activeLang = lang;
};

/** `tr` for non-component code. Does not re-render anything — inside components use `useTr()`. */
export const trNow = <K extends string>(table: L10nTable<K>, key: K, args?: TrArgs): string =>
    translate(activeLang, table, key, args);

/** For keys built at run time (`role_${wire}`): true when the table really has it. */
export const hasKey = <K extends string>(table: L10nTable<K>, key: string): key is K =>
    Object.prototype.hasOwnProperty.call(table.en, key);

/** The bound translator `useTr()` returns; pass it into helpers that live outside components. */
export type Tr = <K extends string>(table: L10nTable<K>, key: K, args?: TrArgs) => string;
